import { semanticColors, typography } from '@moderneinc/design-system-tokens';
import type { InputBaseProps } from '@mui/material';
import { alpha, Box } from '@mui/material';
import React, {
  type ChangeEvent,
  forwardRef,
  type KeyboardEvent,
  useState
} from 'react';
import { parseSearchHighlights } from '../../helpers/search-highlight.helpers';
import { SearchInputField } from './global-search.styled';

type HighlightedSearchInputProps = InputBaseProps & {
  disableChrome?: boolean;
  /**
   * Left padding (theme spacing units) applied IDENTICALLY to the highlight
   * overlay and the real input's text, so the caret lines up with the glyphs.
   */
  textPaddingLeft?: number;
  /**
   * Font size (px) applied IDENTICALLY to the overlay and the input's text,
   * for the same alignment reason.
   */
  textFontSize?: number;
  onKeyDown?: (
    event: KeyboardEvent<HTMLInputElement | HTMLTextAreaElement>
  ) => void;
  onChange?: (
    event: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
  ) => void;
  onBlur?: (
    event: React.FocusEvent<HTMLInputElement | HTMLTextAreaElement>
  ) => void;
};

/**
 * Search input with syntax highlighting for keywords (tags, id, shortName, description, category).
 * Uses forwardRef to support react-hook-form integration and direct DOM access.
 *
 * Handles both ref patterns:
 * - Callback refs: {...register('query')} from react-hook-form
 * - Object refs: ref={useRef()} from parent components
 */
export const HighlightedSearchInput = forwardRef<
  HTMLInputElement,
  HighlightedSearchInputProps
>(
  (
    {
      value,
      onChange,
      onKeyDown,
      onBlur,
      disableChrome,
      textPaddingLeft = 1,
      textFontSize = typography.fontSize.default,
      sx,
      ...restProps
    },
    ref
  ) => {
    const [isFocused, setIsFocused] = useState(false);

    // The value is owned by the parent (React Hook Form). Rendering both the
    // overlay and the real input from that single `value` keeps the highlight
    // glyphs and the caret in lockstep — there is no local mirror to fall
    // behind and nothing to resync.
    const inputValue = (value as string) || '';
    const segments = parseSearchHighlights(inputValue);

    return (
      <Box
        sx={(theme) => {
          const isDark = theme.palette.mode === 'dark';
          // Flat field surface in dark; the decorative light gradient / white
          // focus stay exactly as-is in light mode.
          const focusedBg = isDark
            ? semanticColors.color.surface.raised
            : theme.palette.common.white;
          const restingBg = isDark
            ? semanticColors.color.surface.field
            : `linear-gradient(45deg, ${theme.palette.indigo[50]}, ${theme.palette.newGreys[15]})`;
          return {
            position: 'relative',
            width: '100%',
            ...(!disableChrome && {
              borderRadius: 1,
              border: `1px solid ${semanticColors.color.border.primary}`,
              background: isFocused ? focusedBg : restingBg,
              '&:focus-within': {
                background: focusedBg,
                border: `1px solid ${semanticColors.color.border.focus}`,
                boxShadow: `0 0 0 2px ${alpha(theme.palette.blue[200], 0.4)}`
              }
            })
          };
        }}
      >
        <Box
          aria-hidden="true"
          sx={{
            position: 'absolute',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            py: 0.25,
            // Padding-left and font-size MUST match the real input exactly, or
            // the caret (which tracks the real input's text) drifts from these
            // highlight glyphs — the "cursor one letter behind" bug.
            pl: textPaddingLeft,
            pr: 5,
            fontSize: `${textFontSize}px`,
            pointerEvents: 'none',
            overflow: 'hidden',
            whiteSpace: 'pre',
            display: 'flex',
            alignItems: 'center',
            zIndex: 3, // Must be 3 or higher to avoid blurriness
            WebkitFontSmoothing: 'antialiased',
            MozOsxFontSmoothing: 'grayscale'
          }}
        >
          {segments.map((segment, index) => {
            if (segment.highlighted) {
              // Split the highlighted segment into prefix and value
              const match = segment.text.match(
                /^((?:tags|id|shortName|description|category):)(.*)$/i
              );
              if (match) {
                return (
                  <React.Fragment key={index}>
                    <span
                      style={{
                        color: 'inherit',
                        fontWeight: typography.fontWeight.regular
                      }}
                    >
                      {match[1]}
                    </span>
                    <Box
                      component="span"
                      sx={(theme) => ({
                        color: semanticColors.color.action.default,
                        backgroundColor: alpha(theme.palette.blue.main, 0.15),
                        borderRadius: 0.5,
                        fontWeight: 'inherit',
                        display: 'inline'
                      })}
                    >
                      {match[2]}
                    </Box>
                  </React.Fragment>
                );
              }
              return (
                <Box
                  key={index}
                  component="span"
                  sx={(theme) => ({
                    color: semanticColors.color.action.default,
                    backgroundColor: alpha(theme.palette.blue.main, 0.15),
                    borderRadius: 0.5,
                    fontWeight: 'inherit',
                    display: 'inline'
                  })}
                >
                  {segment.text}
                </Box>
              );
            }
            return (
              <span key={index} style={{ color: 'inherit' }}>
                {segment.text}
              </span>
            );
          })}
        </Box>
        {/* Actual input - completely transparent text */}
        <SearchInputField
          {...restProps}
          ref={(node: HTMLInputElement | null) => {
            if (typeof ref === 'function') {
              ref(node);
            } else if (ref) {
              ref.current = node;
            }
          }}
          value={inputValue}
          onChange={onChange}
          onKeyDown={onKeyDown}
          onFocus={() => setIsFocused(true)}
          onBlur={(e) => {
            setIsFocused(false);
            if (onBlur) {
              onBlur(e);
            }
          }}
          sx={[
            (theme) => ({
              position: 'relative',
              zIndex: 2,
              background: 'transparent !important',
              border: 'none',
              boxShadow: 'none',
              // Keep all left inset on the input element (below) so the caret's
              // text origin equals the overlay's padding-left.
              paddingLeft: 0,
              '&:focus-within': {
                background: 'transparent !important',
                border: 'none',
                boxShadow: 'none'
              },
              '& input': {
                color: 'transparent',
                caretColor: theme.palette.text.primary, // Keep cursor visible
                background: 'transparent',
                WebkitTextFillColor: 'transparent', // Ensure text is transparent in webkit browsers
                textShadow: 'none', // Remove any text shadow
                WebkitTextStroke: '0 transparent', // Ensure no stroke
                // MUST match the overlay exactly (see overlay sx above) so the
                // caret lines up with the highlighted glyphs.
                paddingLeft: theme.spacing(textPaddingLeft),
                fontSize: `${textFontSize}px`
              }
            }),
            // Merge the caller's sx LAST so its overrides (e.g. the placeholder
            // color) win — a bare `sx={...}` here would clobber restProps.sx.
            ...(Array.isArray(sx) ? sx : sx ? [sx] : [])
          ]}
        />
      </Box>
    );
  }
);
